<template>
    <div :class="[variant, { files: files.length }]">
        <div
            v-show="!files.length || variant === 'form'"
            class="attachment-container"
            :class="{ disabled: files.length >= maxFiles }"
            @click="selectFiles"
        >
            <input
                v-if="files.length < maxFiles"
                ref="input"
                class="d-none"
                type="file"
                multiple
                :accept="parsedAcceptedFormats"
                v-bind="attrs"
                v-on="listeners"
                @change="handleFileUpload"
            />
            <span v-if="files.length < maxFiles" class="add-icon-background"
                ><UIIcon :hydrate="false" class="add-icon" :name="['fal', 'plus']"
            /></span>
            <span v-else><UIIcon class="add-icon" :hydrate="false" :name="['fal', 'plus-circle']" /></span>
            <div class="add-text ml-2">
                <p class="mb-0 text" :class="`${variant}-title`">
                    {{ translate(variant === 'orderstatus' ? 'button.orderstatus' : 'button') }}
                </p>
                <small v-if="variant === 'orderstatus'">
                    {{ translate('info.size', { size: convertFileSize(maxSizeTotalBytes) })
                    }}<span v-for="format in acceptedFormats" :key="format" class="format-display">{{
                        format.split('.')[1]
                    }}</span></small
                >
                <small v-else-if="files.length"
                    ><span :class="{ 'text-error': files.length === maxFiles }"
                        >{{ files.length + '/' + maxFiles }} {{ translate('file.multiple') }}</span
                    >
                    - {{ remaining }} {{ translate('remaining') }}</small
                >
                <small v-else-if="!files.length"
                    >{{ translate('info.maxfiles', { maxFiles: maxFiles })
                    }}<span v-if="variant === 'form'"> - {{ translate('info.formats') }} .pdf, .jpg, .png</span></small
                >
            </div>
        </div>
        <div v-if="files.length">
            <hr v-if="variant === 'form'" />
            <div>
                <div
                    v-for="(file, index) in files"
                    :key="file.name"
                    class="file-display"
                    :class="[{ 'mb-3': variant === 'form' }, { 'orderstatus-display': variant === 'orderstatus' }]"
                >
                    <a class="d-flex allign-items-center" @click="removeFile(index)"
                        ><UIIcon
                            class="remove-button"
                            :class="'button-' + variant"
                            :name="['fal', 'times']"
                            :color="variant === 'orderstatus' ? '#007CD3' : ''"
                    /></a>

                    <div class="ml-2">
                        <p class="name mb-0" :class="'name-' + variant">
                            {{ file.name }}
                        </p>
                        <small v-if="variant === 'form'"
                            >{{ convertFileSize(file.size) }} - {{ file.type.split('/')[1] }}</small
                        >
                    </div>
                </div>
            </div>
        </div>
        <div v-if="errorText || topLevelErrors" class="mt-1">
            <small class="error">{{ errorText }}</small>
            <div v-for="(error, index) in topLevelErrors" :key="index">
                <small class="error">{{ error }}</small>
            </div>
        </div>
    </div>
</template>

<script>
import { UIIcon } from '@dundle/ui/components';
import TranslationMixin from '~/mixins/TranslationMixin';

export default {
    components: {
        UIIcon,
    },
    mixins: [TranslationMixin],
    inheritAttrs: false,
    props: {
        maxFiles: {
            type: Number,
            default: 2,
        },
        maxSizeTotalBytes: {
            type: Number,
            default: 10000000,
        },
        topLevelErrors: {
            type: Array,
            required: false,
        },
        variant: {
            type: String,
            default: 'form',
            validator: value => {
                return ['form', 'orderstatus'].includes(value);
            },
        },
        acceptedFormats: {
            type: Array,
            default: () => {
                return ['.jpg', '.jpeg', '.pdf', '.png'];
            },
        },
    },
    data() {
        return {
            files: [],
            errorText: '',
        };
    },
    computed: {
        listeners() {
            const { input, ...listeners } = this.$listeners;
            return listeners;
        },
        attrs() {
            const { ...attrs } = this.$attrs;
            return attrs;
        },
        addedBytes() {
            let addedBytes = 0;
            for (const file of this.files) {
                addedBytes += file.size;
            }
            return addedBytes;
        },
        remaining() {
            return this.convertFileSize(this.maxSizeTotalBytes - this.addedBytes);
        },
        parsedAcceptedFormats() {
            return this.acceptedFormats.join(',');
        },
    },
    methods: {
        handleFileUpload() {
            if (this.files.length >= this.maxFiles) return;

            for (const file of this.$refs.input.files) {
                if (this.files.length >= this.maxFiles) {
                    this.errorText = this.translate('errors.maxAmount', { maxFiles: this.maxFiles });
                    break;
                }
                if (this.files.find(f => f.name === file.name)) {
                    this.errorText = this.translate('errors.duplicate');
                    return;
                }
                if (this.addedBytes + file.size > this.maxSizeTotalBytes) {
                    this.errorText = this.translate('errors.totalSize');
                    return;
                }
                this.errorText = '';
                this.files.push(file);
            }
            this.$emit('update:files', this.files);
            this.$emit('updated');
        },
        convertFileSize(bytes, decimalPoint) {
            if (bytes === 0) return '0 Bytes';
            const k = 1000;
            const dm = decimalPoint || 2;
            const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
        },
        selectFiles() {
            if (this.files.length < this.maxFiles) {
                this.$refs.input.click();
            }
        },
        removeFile(index) {
            this.errorText = '';
            this.files.splice(index, 1);
        },
    },
};
</script>

<style lang="scss" scoped>
.orderstatus {
    border: 1px solid $color-grey-4;
    border-radius: 6px;
    padding: 1rem;
    background-color: RGBA(203, 213, 216, 0.15);

    &.files {
        padding: 0.73rem;
        border: none;
    }

    &:hover {
        background-color: RGBA(203, 213, 216, 0.3);
    }
}

.attachment-container {
    display: flex;
    position: relative;

    &:hover {
        cursor: pointer;
    }

    &.disabled {
        cursor: auto;
    }

    .add-icon-background {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: $color-button-secondary;

        &:hover {
            background-color: $color-button-secondary-hover;
        }

        min-width: 35px;
        height: 35px;
        border-radius: 50%;
    }

    .add-icon {
        color: white;

        width: 20px;
        height: 20px;
        margin-left: 1px;
    }
}

.add-text {
    display: flex;
    flex-direction: column;
    justify-content: center;

    .text {
        line-height: 1.2rem;
        font-weight: bold;
    }

    .orderstatus-title {
        font-weight: 400;
        color: $color-primary-dark;
    }

    .format-display {
        margin-left: 0.5rem;
        padding: 0.1rem 0.2rem;
        color: #865387;
        background-color: #8653872c;
        border: 1px solid #865387;
        border-radius: 4px;
        text-transform: uppercase;
    }

    .text-error {
        color: $color-error;
    }
}

.name {
    .name-form {
        font-size: 0.867rem;
        line-height: 0.867rem;
    }
}

.max-warning {
    font-size: 0.6rem;
}

.file-display {
    display: flex;
    align-items: center;
}

.orderstatus-display {
    flex-direction: row-reverse;
    justify-content: space-between;
}

.remove-button {
    font-size: 1.2rem;
    margin-right: 5px;

    &.button-orderstatus {
        font-size: 1.5rem;
    }

    &:hover {
        cursor: pointer;
        filter: invert(42%) sepia(97%) saturate(1376%) hue-rotate(162deg) brightness(95%) contrast(101%);
    }
}

.error {
    color: $color-error;
}
</style>

<i18n></i18n>
